/** The words of the activity's Documents & links panel (plan §3 "Documents & links", O12):
 *  how many tour stops inherit the documents, what each row is, and why a write was refused.
 *  Pure. */
import type { ActivityDocument } from '@/api/activityDocuments';
import { ACTIVITY_DOCUMENT_MAX_BYTES } from '@/api/activityDocuments';
import { catalogWriteErrors } from '@/api/catalogWriteErrors';
import { ConsoleApiError } from '@/api/client';

/** The panel's caption after "Saved immediately": the tour stops that show these documents
 *  ("Use activity documents" on a live tour's activity row). */
export function inheritedText(n: number): string {
  if (n <= 0) return 'no tour stop inherits them';
  return `inherited by ${n.toLocaleString('en-US')} tour ${n === 1 ? 'stop' : 'stops'}`;
}

/** What removing a document does, said before it is done. */
export function removeText(inherited: number): string {
  if (inherited <= 0) return 'The app stops showing it at once.';
  const stops = `${inherited.toLocaleString('en-US')} tour ${inherited === 1 ? 'stop' : 'stops'}`;
  return `The app stops showing it at once — here and on the ${stops} that inherit it.`;
}

const FILE_KINDS: ReadonlyArray<[RegExp, string]> = [
  [/^application\/pdf$/, 'PDF'],
  [/^image\//, 'Image'],
  [/^video\//, 'Video'],
  [/^audio\//, 'Audio'],
  [/^text\/plain$/, 'Text'],
  [/(word|opendocument\.text)/, 'Document'],
  [/(excel|spreadsheet)/, 'Spreadsheet'],
  [/(powerpoint|presentation)/, 'Presentation'],
];

/** A stored file's kind in a word, from its sniffed type ("PDF", "Image"); "File" else. */
export function fileKind(contentType: string | null | undefined): string {
  if (!contentType) return 'File';
  return FILE_KINDS.find(([re]) => re.test(contentType))?.[1] ?? 'File';
}

/** A link's host ("nps.gov"), else its address. */
export function linkHost(url: string | null | undefined): string {
  if (!url) return 'Link';
  try {
    return new URL(url).host.replace(/^www\./, '') || url;
  } catch {
    return url;
  }
}

/** A row's facts after its name: what it is, then how the app opens it. */
export function documentFacts(doc: ActivityDocument): string[] {
  const facts = [doc.type === 'link' ? linkHost(doc.url) : fileKind(doc.content_type)];
  if (doc.type === 'file' && doc.pending_download) facts.push('not downloaded yet');
  if (doc.deep_link) facts.push('opens in the app');
  if (doc.is_360_degree) facts.push('360°');
  return facts;
}

/** Why a file is not sent at all: over the documents' cap. */
export function tooLargeText(file: Pick<File, 'name' | 'size'>): string | null {
  return file.size > ACTIVITY_DOCUMENT_MAX_BYTES
    ? `“${file.name}” is larger than 32 MB, the most a document may be.`
    : null;
}

/** A refused write in the server's words (`{detail, code, field?}`, a 413), the field it
 *  names first; `fallback` for anything without words. */
export function documentErrorText(e: unknown, fallback: string): string {
  const split = catalogWriteErrors(e);
  const named = split ? Object.values(split.fields)[0] : undefined;
  if (named) return named;
  if (split?.form) return split.form;
  if (e instanceof ConsoleApiError && e.status === 413) return 'The file is larger than 32 MB, the most a document may be.';
  if (e instanceof ConsoleApiError && e.status === 403) return 'You may not change this activity’s documents.';
  return e instanceof Error && e.message ? `${fallback} ${e.message}` : fallback;
}
